import { useRouterState } from "@tanstack/react-router"

const EMPTY_PARAMS: Record<string, string> = {}
const EMPTY_SEARCH: Record<string, string | undefined> = {}

const selectLeafParams = (state: { matches: Array<{ params: Record<string, string> }> }) =>
  state.matches[state.matches.length - 1]?.params ?? EMPTY_PARAMS

const selectLeafSearch = (state: {
  matches: Array<{ search: Record<string, string | undefined> }>
}) => state.matches[state.matches.length - 1]?.search ?? EMPTY_SEARCH

// Route-agnostic accessor for path params. Mirrors TanStack's
// `useParams({ strict: false })` — returns whatever path params are bound on
// the currently-active leaf match. Used by shared components rendered across
// multiple routes (HOCs, Navigation, AppSwitcher, breadcrumbs, generic
// filters).
//
// On pages with a known route at compile time, prefer the route's own typed
// hook: `markersIndexRoute.useParams()` returns
// `{ organizationSlug: string; appId: string }`.
export const useCurrentParams = (): Record<string, string | undefined> => {
  return useRouterState({ select: selectLeafParams })
}

const requireCurrentParam = (params: Record<string, string | undefined>, key: string): string => {
  const value = params[key]

  if (!value) {
    throw new Error(`Missing required route param: ${key}`)
  }

  return value
}

export const useAppId = (): string => {
  return requireCurrentParam(useCurrentParams(), "appId")
}

export const useOrgSlug = (): string => {
  return requireCurrentParam(useCurrentParams(), "organizationSlug")
}

// Route-agnostic accessor for search params. Mirrors TanStack's
// `useSearch({ strict: false })`. See `useCurrentParams` for usage guidance.
export const useCurrentSearch = (): Record<string, string | undefined> => {
  return useRouterState({ select: selectLeafSearch })
}

// Rebuild a match's search as a plain, string-valued object, dropping empty
// values. Two reasons, both about handing search to code that doesn't expect
// TanStack's shape — reading individual keys off `useCurrentSearch()` needs
// neither:
//
//   - **Values are normalized.** `undefined`/`null` are dropped and everything
//     else is `String()`ed, so a consumer that appends every key to a query
//     string can't emit `?key=undefined`.
//   - **The result has `Object.prototype`.** TanStack builds a match's search
//     with `Object.create(null)`, so this function returns a normal object for
//     consumers that pass the result to code that expects a plain object.
//
// Not a hook, so a suite that replaces this module with a `jest.mock` factory
// has to spread `jest.requireActual` into it rather than listing only the hooks
// it overrides.
export const toPlainSearch = (search: Record<string, unknown> = {}): Record<string, string> => {
  const plain: Record<string, string> = {}
  for (const key in search) {
    const value = search[key]
    if (value !== undefined && value !== null) {
      plain[key] = String(value)
    }
  }
  return plain
}
